import imgP1 from '@/assets/project-3d-yaninda.webp';
import imgP2 from '@/assets/project-ailem-aselsan.webp';
import imgP3 from '@/assets/project-outfica.webp';
import imgP4 from '@/assets/project-ztech.webp';
import imgP5 from '@/assets/project-royal-seginus.webp';
import imgP6 from '@/assets/project-kolektif.webp';
import imgP7 from '@/assets/project-lcw.webp';
import imgP8 from '@/assets/project-evarkadasi.webp';
import imgP9 from '@/assets/project-bool.webp';
import imgP10 from '@/assets/project-backdoor-tickets.webp';
import imgP1Extra1 from '@/assets/project-1.webp';
import imgP1Extra2 from '@/assets/project-2.webp';
import imgP4Extra1 from '@/assets/project-4.webp';
import imgP4Extra2 from '@/assets/project-3.webp';
import imgP6Extra1 from '@/assets/project-2.webp';
import imgP6Extra2 from '@/assets/project-3.webp';

export type PortfolioProjectKey =
  | 'p1'
  | 'p2'
  | 'p3'
  | 'p4'
  | 'p5'
  | 'p6'
  | 'p7'
  | 'p8'
  | 'p9'
  | 'p10';

export type PortfolioProject = {
  key: PortfolioProjectKey;
  slug: string;
  image: string;
  liveUrl?: string;
};

/** Canonical SEO slugs for portfolio case studies. */
export const portfolioProjects: PortfolioProject[] = [
  { key: 'p1', slug: '3d-yaninda', image: imgP1, liveUrl: 'https://3dyaninda.com/' },
  { key: 'p2', slug: 'ailem-aselsan', image: imgP2, liveUrl: 'https://ailemaselsan.com.tr/giris' },
  { key: 'p3', slug: 'outfica', image: imgP3, liveUrl: 'https://outfica.com/' },
  { key: 'p4', slug: 'ztech', image: imgP4, liveUrl: 'https://ztechedu.co/tr' },
  { key: 'p5', slug: 'royal-seginus', image: imgP5, liveUrl: 'https://www.royalseginus.com/' },
  { key: 'p6', slug: 'kolektif', image: imgP6, liveUrl: 'https://www.kolektiffuarcilik.com/' },
  { key: 'p7', slug: 'lcw', image: imgP7 },
  { key: 'p8', slug: 'ev-arkadasi', image: imgP8, liveUrl: 'https://evarkadasi.com/' },
  { key: 'p9', slug: 'bool', image: imgP9, liveUrl: 'https://bool.com.tr/' },
  { key: 'p10', slug: 'backdoor-tickets', image: imgP10 },
];

export function getProjectLiveUrl(key: PortfolioProjectKey): string | undefined {
  return portfolioProjects.find((p) => p.key === key)?.liveUrl;
}

const slugToKey = new Map(portfolioProjects.map((p) => [p.slug, p.key]));
const keyToSlug = new Map(portfolioProjects.map((p) => [p.key, p.slug]));

/** Per-project gallery assets (empty = hero only on detail page). */
export const projectDetailGallery: Record<PortfolioProjectKey, string[]> = {
  p1: [imgP1Extra1, imgP1Extra2],
  p2: [],
  p3: [],
  p4: [imgP4Extra1, imgP4Extra2],
  p5: [],
  p6: [imgP6Extra1, imgP6Extra2],
  p7: [],
  p8: [],
  p9: [],
  p10: [],
};

/** Legacy catalog / CMS slugs → portfolio keys (301 targets use canonical slug). */
export const legacyProjectSlugAliases: Record<string, PortfolioProjectKey> = {
  'fintrack-ai': 'p4',
  medisync: 'p2',
  eduverse: 'p6',
  logichain: 'p7',
  p1: 'p1',
  p2: 'p2',
  p3: 'p3',
  p4: 'p4',
  p5: 'p5',
  p6: 'p6',
  p7: 'p7',
  p8: 'p8',
  p9: 'p9',
  p10: 'p10',
};

export function getPortfolioProjectSlug(key: PortfolioProjectKey): string {
  return keyToSlug.get(key) ?? key;
}

export function resolvePortfolioProjectKey(idOrSlug: string | undefined): PortfolioProjectKey | null {
  if (!idOrSlug) return null;
  if (slugToKey.has(idOrSlug)) return slugToKey.get(idOrSlug)!;
  if (legacyProjectSlugAliases[idOrSlug]) return legacyProjectSlugAliases[idOrSlug];
  return null;
}

export function getCanonicalProjectPath(idOrSlug: string): string | null {
  const key = resolvePortfolioProjectKey(idOrSlug);
  if (!key) return null;
  return `/projects/${getPortfolioProjectSlug(key)}`;
}

export function readPortfolioProjectSlugs(): string[] {
  return portfolioProjects.map((p) => p.slug).sort();
}


export const projectMainImage: Record<PortfolioProjectKey, string> = {
  p1: imgP1,
  p2: imgP2,
  p3: imgP3,
  p4: imgP4,
  p5: imgP5,
  p6: imgP6,
  p7: imgP7,
  p8: imgP8,
  p9: imgP9,
  p10: imgP10,
};

/** Intrinsic size/MIME of `projectMainImage` files (used for og:image:*); a test re-reads the files to keep this in sync. */
export const projectMainImageMeta: Record<PortfolioProjectKey, { file: string; width: number; height: number; type: string }> = {
  p1: { file: 'project-3d-yaninda.webp', width: 1024, height: 552, type: 'image/webp' },
  p2: { file: 'project-ailem-aselsan.webp', width: 1024, height: 528, type: 'image/webp' },
  p3: { file: 'project-outfica.webp', width: 1024, height: 640, type: 'image/webp' },
  p4: { file: 'project-ztech.webp', width: 1024, height: 534, type: 'image/webp' },
  p5: { file: 'project-royal-seginus.webp', width: 1024, height: 535, type: 'image/webp' },
  p6: { file: 'project-kolektif.webp', width: 1024, height: 532, type: 'image/webp' },
  p7: { file: 'project-lcw.webp', width: 1024, height: 536, type: 'image/webp' },
  p8: { file: 'project-evarkadasi.webp', width: 1024, height: 535, type: 'image/webp' },
  p9: { file: 'project-bool.webp', width: 1024, height: 537, type: 'image/webp' },
  p10: { file: 'project-backdoor-tickets.webp', width: 1024, height: 534, type: 'image/webp' },
};
